import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import emailjs from 'emailjs-com';
import { motion } from 'framer-motion';
import { ArrowUpRight, Check, MessageCircle, Phone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useNavigate } from 'react-router-dom';
import FacebookReviews from '@/components/shared/FacebookReviews';
import VideoTestimonial from '@/components/home/VideoTestimonial';

const GoogleAdsManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [replyTo, setReplyTo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [budget, setBudget] = useState('');
  const [showFloating, setShowFloating] = useState(false);
  const whatsappUrl = 'https://wa.me/12026998621';
  const EMAILJS_USER_ID = 'IiRfsZbuWCZHwgBlt';

  useEffect(() => {
    const handleScroll = () => setShowFloating(window.scrollY > 600);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById('audit-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    setIsSubmitting(true);
    const form = formRef.current;

    try {
      await emailjs.sendForm('service_db1w009', 'template_0dqc2ot', form, EMAILJS_USER_ID);
      toast({
        title: 'Got it.',
        description: "I'll review and reply within 24 hours.",
      });
      form.reset();
      setReplyTo('');
      setBudget('');
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: 'Could you try again or email me directly?',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "h-14 rounded-2xl border-[#ddd9cc] bg-[#f5f4f2] px-4 text-base text-slate-700 shadow-[0_2px_8px_rgba(15,23,42,0.05)] placeholder:text-slate-500 focus-visible:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:placeholder:text-slate-400";

  return (
    <>
      <Helmet>
        <title>Google Ads Management, $300/mo Flat | Dallas & US | Omnicraft</title>
        <meta name="description" content="Flat $300/month Google Ads management for Dallas–Fort Worth and US small businesses: campaigns, PPC and Shopping Ads. No contracts. Free audit." />
        <meta name="keywords" content="google ads management services, google ads management company, google ads management service, google ads management near me, google ads campaign management, google ads agency, google ads management agency, google ads ppc management, google shopping ads management, google ads management dallas, google ads agency dallas, google ads management dfw, google ads management for small business" />
        <meta property="og:title" content="Google Ads Management, $300/month flat | Dallas & US" />
        <meta property="og:description" content="Google Ads management for Dallas–Fort Worth and US small businesses. Flat $300/month. No contracts. Free audit." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.omnicraftservices.com/google-ads-management" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google Ads Management, $300/month flat | Dallas & US" />
        <meta name="twitter:description" content="Google Ads management for Dallas–Fort Worth and US businesses. Flat $300/month. No contracts. Free audit." />
        <link rel="canonical" href="https://www.omnicraftservices.com/google-ads-management" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Google Ads Management Services",
          "name": "Google Ads Management Services",
          "alternateName": ["Google Ads Management Company", "Google Ads Management Agency", "Google Ads PPC Management"],
          "description": "Google Ads management services including campaign management, PPC management, and Google Shopping Ads management. Flat $300/month, no contracts.",
          "provider": { "@id": "https://www.omnicraftservices.com/#organization" },
          "areaServed": [
            { "@type": "Country", "name": "United States" },
            { "@type": "City", "name": "Dallas", "containedInPlace": { "@type": "State", "name": "Texas" } },
            { "@type": "City", "name": "Fort Worth", "containedInPlace": { "@type": "State", "name": "Texas" } }
          ],
          "offers": {
            "@type": "Offer",
            "price": "300",
            "priceCurrency": "USD",
            "priceSpecification": {
              "@type": "UnitPriceSpecification",
              "price": "300",
              "priceCurrency": "USD",
              "billingDuration": "P1M"
            }
          }
        })}</script>
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#fbfaf5] text-slate-900 dark:bg-slate-950 dark:text-white">
        {/* Soft brand orbs (matches Affordable page) */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-28 left-[-10%] h-[34rem] w-[34rem] rounded-full bg-emerald-300/45 blur-3xl dark:bg-emerald-400/12" />
          <div className="absolute top-16 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-amber-200/40 blur-3xl dark:bg-amber-300/12" />
          <div className="absolute bottom-[-10rem] left-[14%] h-[30rem] w-[30rem] rounded-full bg-lime-200/42 blur-3xl dark:bg-lime-400/12" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.98),_rgba(255,255,255,0.78)_22%,_rgba(251,250,245,0.5)_55%,_rgba(243,249,239,0.32)_75%,_rgba(243,249,239,0)_100%)] dark:bg-[radial-gradient(circle_at_top,_rgba(15,23,42,0.7),_rgba(15,23,42,0.86)_45%,_rgba(2,6,23,1)_100%)]" />
        </div>

        <div className="relative z-10">
          {/* HERO — headline + form side by side */}
          <section className="px-4 pt-28 pb-12 sm:pt-32 md:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16 items-start">
                {/* Left — copy */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight text-slate-950 md:text-6xl dark:text-white">
                    Google Ads Management Services
                  </h1>

                  <p className="mt-4 text-base font-medium text-emerald-700 dark:text-emerald-400">
                    A small Google Ads management company serving Dallas–Fort Worth and businesses across the US: campaign management, PPC, Shopping Ads.
                  </p>

                  <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
                    Flat <span className="font-semibold text-slate-900 dark:text-white">$300/month</span>. No percentage of spend, no annual contracts, no auto-generated reports. One person tuning your campaigns every week.
                  </p>

                  <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 font-semibold text-emerald-700 underline decoration-2 underline-offset-4 dark:text-emerald-400"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </a>
                    <span className="text-slate-500 dark:text-slate-400">Replies within 24h on weekdays</span>
                  </div>

                  {/* Quick proof row */}
                  <div className="mt-12 grid grid-cols-3 gap-4 border-t border-[#e7e0cf] pt-8 dark:border-white/10">
                    <div>
                      <div className="text-3xl font-semibold text-slate-900 dark:text-white">9.2%</div>
                      <div className="mt-1 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Avg. CTR</div>
                    </div>
                    <div>
                      <div className="text-3xl font-semibold text-slate-900 dark:text-white">$3.56</div>
                      <div className="mt-1 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Avg. CPC</div>
                    </div>
                    <div>
                      <div className="text-3xl font-semibold text-slate-900 dark:text-white">3</div>
                      <div className="mt-1 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Active accounts</div>
                    </div>
                  </div>
                </motion.div>

                {/* Right — form */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  id="audit-form"
                >
                  <div className="rounded-[2.25rem] border border-[#e7e0cf] bg-[#f8f5ec]/96 p-6 shadow-[0_30px_90px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/65 sm:p-8">
                    <div className="mb-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                        Free audit
                      </p>
                      <h2 className="mt-2 text-2xl font-semibold text-slate-950 dark:text-white">
                        Send your details. I'll send back a 1-page audit.
                      </h2>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                        No obligation. If your account is already healthy I'll tell you that too.
                      </p>
                    </div>

                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Name</Label>
                        <Input id="name" name="name" required placeholder="Your name" className={inputClass} />
                      </div>

                      <div>
                        <Label htmlFor="business_name" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Business</Label>
                        <Input id="business_name" name="business_name" required placeholder="What you sell" className={inputClass} />
                      </div>

                      <div>
                        <Label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="you@business.com"
                          value={replyTo}
                          onChange={(e) => setReplyTo(e.target.value)}
                          className={inputClass}
                        />
                        <input type="hidden" name="reply_to" value={replyTo} />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Phone</Label>
                        <Input id="phone" name="phone" required placeholder="+1 (XXX) XXX-XXXX" className={inputClass} />
                      </div>

                      <div>
                        <Label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">Monthly ad budget</Label>
                        <Select value={budget} onValueChange={setBudget}>
                          <SelectTrigger className="h-14 rounded-2xl border-[#ddd9cc] bg-[#f5f4f2] px-4 text-base text-slate-700 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100">
                            <SelectValue placeholder="Pick a range" />
                          </SelectTrigger>
                          <SelectContent className="z-[60]">
                            <SelectItem value="Under $500">Under $500</SelectItem>
                            <SelectItem value="$500–$1,000">$500–$1,000</SelectItem>
                            <SelectItem value="$1,000–$3,000">$1,000–$3,000</SelectItem>
                            <SelectItem value="$3,000+">$3,000+</SelectItem>
                          </SelectContent>
                        </Select>
                        <input type="hidden" name="budget" value={budget} />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="h-14 w-full rounded-2xl bg-black text-base font-medium text-white shadow-[0_16px_34px_rgba(0,0,0,0.45)] transition-colors duration-300 hover:bg-[#121212]"
                      >
                        {isSubmitting ? 'Sending…' : 'Get my free audit'}
                      </Button>
                      <p className="pt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                        Free · No obligation · 24-hour reply
                      </p>
                    </form>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* CLIENT REVIEWS — Facebook embeds */}
          <section className="px-4 pt-12 md:px-8">
            <div className="mx-auto max-w-6xl mb-2">
              <p className="text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                — 01 / Words from clients
              </p>
            </div>
            <FacebookReviews
              heading="Recommendations from real clients."
              subheading="Direct from their own Facebook profiles. Click through to read more on their pages."
            />
          </section>

          {/* VIDEO TESTIMONIALS */}
          <section className="px-4 pt-12 md:px-8">
            <div className="mx-auto max-w-6xl mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                — 02 / Watch them say it
              </p>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold leading-tight text-slate-950 dark:text-white max-w-2xl">
                Client video testimonials.
              </h2>
            </div>
            <VideoTestimonial />
          </section>

          {/* WEEK BY WEEK */}
          <section className="px-4 py-20 md:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
                <div className="md:col-span-5">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                    — 03 / The work
                  </p>
                  <h2 className="text-3xl md:text-4xl font-semibold leading-tight text-slate-950 dark:text-white">
                    What $300 actually buys.
                  </h2>
                </div>
                <div className="md:col-span-6 md:col-start-7 self-end">
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    Every line below is something I do, by hand, every week or every month. Nothing is white-labeled, nothing is automated.
                  </p>
                </div>
              </div>

              <div className="rounded-[2rem] border border-[#e7e0cf] bg-white/70 backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/40 overflow-hidden">
                {[
                  { week: 'Week 1', title: 'Account setup, end to end', desc: 'Conversion tracking, audience baselines, negative keyword seed list, ad extensions, geo-targeting. Built inside your account so you own it.' },
                  { week: 'Week 1', title: 'Keyword research', desc: 'I write the list by hand from search-term data — not a tool dump. Includes match-type strategy and a kill-list for the obvious budget burners.' },
                  { week: 'Week 1', title: 'Ad copy, written by a human', desc: 'Three to five variants per ad group. Headlines tied to specific user intent, not "Best [keyword] | Click Now."' },
                  { week: 'Weekly', title: 'Search-term review & negatives', desc: 'I read your search-term report every week and add negatives so your spend stops leaking into junk queries.' },
                  { week: 'Weekly', title: 'Bid & budget tuning', desc: "Adjustments based on what is actually converting — not Google's recommended optimizations, which usually want you to spend more." },
                  { week: 'Monthly', title: 'A real report, written by me', desc: "A short note explaining what worked, what didn't, and what I'm changing. No 14-tab dashboard you'll never open." },
                ].map((item, idx, arr) => (
                  <div
                    key={idx}
                    className={`grid grid-cols-12 gap-4 md:gap-8 p-6 md:p-8 ${idx < arr.length - 1 ? 'border-b border-[#e7e0cf] dark:border-white/10' : ''} hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 transition-colors`}
                  >
                    <div className="col-span-12 md:col-span-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400">
                      {item.week}
                    </div>
                    <div className="col-span-12 md:col-span-4">
                      <h3 className="text-xl font-semibold text-slate-950 dark:text-white">
                        {item.title}
                      </h3>
                    </div>
                    <div className="col-span-12 md:col-span-6 text-slate-600 dark:text-slate-400 leading-relaxed">
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CASE STUDIES */}
          <section className="px-4 py-20 md:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="mb-12">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                  — 04 / Recent work
                </p>
                <h2 className="text-3xl md:text-4xl font-semibold leading-tight text-slate-950 dark:text-white max-w-2xl">
                  A few campaigns we've run.
                </h2>
                <p className="mt-3 text-slate-600 dark:text-slate-400 max-w-xl">
                  Real businesses. Real numbers. Click any tile to see the full breakdown.
                </p>
              </div>

              {/* Featured — In-house test with screenshot */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6 }}
                className="mb-8 rounded-[2rem] border border-[#e7e0cf] bg-white/80 p-6 md:p-10 shadow-[0_16px_40px_rgba(15,23,42,0.06)] dark:border-white/10 dark:bg-slate-900/40"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                  <div>
                    <div className="mb-6 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:text-emerald-400">
                        Google Ads — In-house test
                      </span>
                      <span className="text-xs text-slate-500">3 weeks</span>
                    </div>

                    <h3 className="text-3xl md:text-4xl font-semibold leading-tight text-slate-950 dark:text-white mb-8">
                      9.2<span className="text-emerald-600">%</span> CTR from a cold-start account.
                    </h3>

                    <div className="grid grid-cols-2 gap-5 mb-8 pb-8 border-b border-[#e7e0cf] dark:border-white/10">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Impressions</div>
                        <div className="text-2xl font-semibold text-slate-900 dark:text-white">674</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Clicks</div>
                        <div className="text-2xl font-semibold text-slate-900 dark:text-white">62</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">CTR</div>
                        <div className="text-2xl font-semibold text-emerald-600">9.2%</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Avg. CPC</div>
                        <div className="text-2xl font-semibold text-slate-900 dark:text-white">$3.56</div>
                      </div>
                    </div>

                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                      Ran on our own brand to prove the process from scratch. Zero historical data, no warm audiences. The CTR landed roughly 3× the Google paid-search benchmark — tight keyword grouping and ad copy written for the actual intent behind each query.
                    </p>
                  </div>

                  <div className="rounded-2xl overflow-hidden border border-[#e7e0cf] dark:border-white/10 bg-white shadow-[0_16px_40px_rgba(15,23,42,0.08)]">
                    <img
                      src="/omnicraft-ads.png"
                      alt="OmniCraft Google Ads dashboard — 9.2% CTR, $3.56 average CPC"
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Three small SEOteric-style tiles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    to: '/case-study/kogit',
                    siteLabel: 'learnkogit.com',
                    label: 'Web + Marketing — Kogit Labs',
                    image: '/kogit.png',
                    desc: 'Built and marketed the IT-simulation platform end to end. Cold start to thousands of monthly active users across 7 countries.',
                    metric: '6.9K',
                    metricLabel: 'Active users / month',
                  },
                  {
                    href: 'https://www.bakerandhmediaproductions.com/',
                    siteLabel: 'bakerandhmediaproductions.com',
                    label: 'Website + SEO — Baker Production',
                    image: '/baker.png',
                    desc: 'Custom-built website plus SEO and conversion advice for the production studio. Inquiries up, bounce rate down.',
                    metric: '3X',
                    metricLabel: 'Increase in inquiries',
                  },
                  {
                    href: 'https://ledgertreefinancialgroup.com/',
                    siteLabel: 'ledgertreefinancialgroup.com',
                    label: 'Website + Branding — Ledger Tree',
                    image: '/ledgertree.png',
                    desc: 'Custom WordPress build with secure client portal and SEO for a financial services firm. Direct + organic traffic up sharply.',
                    metric: '4X',
                    metricLabel: 'More qualified leads',
                  },
                ].map((tile, idx) => {
                  const cardInner = (
                    <>
                      <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-slate-800">
                        <img
                          src={tile.image}
                          alt={tile.label}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        {tile.href && (
                          <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-white/90 dark:bg-slate-900/90 backdrop-blur px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 shadow">
                            Visit site
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7" /><path d="M7 7h10v10" /></svg>
                          </span>
                        )}
                      </div>
                      <div className="p-7 flex flex-col flex-1">
                        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-400 mb-2">
                          {tile.label}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mb-4 font-mono break-all group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                          {tile.siteLabel}
                        </div>
                        <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-8 flex-1">
                          {tile.desc}
                        </p>
                        <div className="pt-6 border-t border-[#e7e0cf] dark:border-white/10">
                          <div className="text-5xl md:text-6xl font-semibold text-slate-950 dark:text-white leading-none mb-2 group-hover:text-emerald-700 dark:group-hover:text-emerald-400 transition-colors">
                            {tile.metric}
                          </div>
                          <div className="text-xs uppercase tracking-wider text-slate-500">
                            {tile.metricLabel}
                          </div>
                        </div>
                      </div>
                    </>
                  );

                  const sharedClass = "text-left block rounded-[1.75rem] border border-[#e7e0cf] bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.06)] hover:border-emerald-400 hover:-translate-y-1 dark:border-white/10 dark:bg-slate-900/40 dark:hover:border-emerald-500/60 transition-all group flex flex-col overflow-hidden";
                  const motionProps = {
                    initial: { opacity: 0, y: 20 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, margin: '-80px' },
                    transition: { duration: 0.6, delay: idx * 0.1 },
                  } as const;

                  if (tile.href) {
                    return (
                      <motion.a
                        key={idx}
                        href={tile.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={sharedClass}
                        {...motionProps}
                      >
                        {cardInner}
                      </motion.a>
                    );
                  }
                  return (
                    <motion.button
                      key={idx}
                      onClick={() => tile.to && navigate(tile.to)}
                      className={sharedClass}
                      {...motionProps}
                    >
                      {cardInner}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* PRICING */}
          <section className="px-4 py-20 md:px-8">
            <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-12 gap-8">
              <div className="md:col-span-4">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                  — 05 / Pricing
                </p>
                <h2 className="text-3xl md:text-4xl font-semibold leading-tight text-slate-950 dark:text-white mb-4">
                  One number. No surprises.
                </h2>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                  I charge a flat fee because performance pricing rewards agencies for spending more, not for spending well. Your ad budget goes straight to Google. My fee is mine.
                </p>
              </div>

              <div className="md:col-span-7 md:col-start-6">
                <div className="rounded-[2rem] border border-[#dfe8d8] bg-white/80 p-8 md:p-10 shadow-[0_16px_40px_rgba(15,23,42,0.08)] dark:border-emerald-900/60 dark:bg-emerald-950/25">
                  <div className="mb-8 pb-8 border-b border-[#e7e0cf] dark:border-white/10 flex items-baseline justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:text-emerald-400 mb-2">
                        Starter management
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl md:text-6xl font-semibold text-slate-950 dark:text-white">$300</span>
                        <span className="text-slate-500">/month</span>
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <div>Cancel anytime</div>
                      <div>30 days notice</div>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {[
                      'Full campaign build inside your account',
                      'Keyword research + match-type strategy',
                      'Ad copy written by hand',
                      'Weekly search-term review & negative keywords',
                      'Conversion tracking setup',
                      'Monthly written report — no dashboard theater',
                      'Direct line to me. Email, Slack, phone — your choice.',
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                        <span className="text-slate-700 dark:text-slate-200">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => document.getElementById('audit-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="h-14 w-full rounded-2xl bg-black text-base font-medium text-white shadow-[0_16px_34px_rgba(0,0,0,0.45)] transition-colors duration-300 hover:bg-[#121212]"
                  >
                    Start with a free audit
                  </Button>
                  <p className="pt-3 text-center text-xs text-slate-500">
                    Your Google Ads account stays in your name. Always.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="px-4 py-20 md:px-8">
            <div className="mx-auto max-w-6xl">
              <div className="mb-14 text-center">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.34em] text-emerald-700 dark:text-emerald-400">
                  — 06 / FAQ
                </p>
                <h2 className="mx-auto max-w-3xl text-3xl md:text-5xl font-semibold leading-tight text-slate-950 dark:text-white">
                  Things people ask before they sign.
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 dark:text-slate-400">
                  No mystery. No fine print. If your question isn't here, just call.
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {[
                  { q: 'Do I need a Google Ads account already?', a: 'No. I build everything inside a fresh account in your name. You own it from day one — billing, history, and admin access all stay with you.' },
                  { q: "What's not included in the $300?", a: 'Your ad spend. That goes directly from your card to Google. The $300 is purely my fee for managing the account — no markups, no percentage of spend.' },
                  { q: 'How fast do I see results?', a: 'First impressions usually show within 24 hours of launch. Leads typically start in the first 7–10 days, depending on your industry and budget. I will tell you if I think it will take longer.' },
                  { q: 'Can I cancel?', a: 'Yes, with 30 days notice. No annual contracts, no early-termination fees. If it is not working, you should not be locked in.' },
                  { q: 'Do I keep my account if we part ways?', a: 'Always. Your account is in your name, your billing details are yours. I never hold accounts hostage — that should be illegal, frankly.' },
                ].map((item, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`item-${idx}`}
                    className="rounded-[1.75rem] border border-[#e7e0cf] bg-white/80 px-6 md:px-8 shadow-[0_8px_24px_rgba(15,23,42,0.04)] data-[state=open]:shadow-[0_16px_40px_rgba(15,23,42,0.08)] data-[state=open]:border-emerald-300 dark:border-white/10 dark:bg-slate-900/40 dark:data-[state=open]:border-emerald-500/40 transition-all"
                  >
                    <AccordionTrigger className="group py-6 text-left hover:no-underline">
                      <div className="flex items-start gap-4 md:gap-6 flex-1">
                        <span className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 tabular-nums">
                          Q.{String(idx + 1).padStart(2, '0')}
                        </span>
                        <span className="text-lg md:text-xl font-semibold text-slate-950 group-hover:text-emerald-700 dark:text-white dark:group-hover:text-emerald-400 transition-colors">
                          {item.q}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pl-0 md:pl-[5.5rem] text-base leading-relaxed text-slate-600 dark:text-slate-300">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Still curious? */}
              <div className="mt-14 rounded-[2rem] border border-[#e7e0cf] bg-[#f8f5ec]/80 p-8 md:p-10 text-center dark:border-white/10 dark:bg-slate-900/40">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:text-emerald-400 mb-3">
                  Still curious?
                </p>
                <h3 className="text-2xl md:text-3xl font-semibold text-slate-950 dark:text-white mb-3">
                  Pick up the phone — I answer myself.
                </h3>
                <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
                  Five minutes is usually all it takes to figure out if we're a fit.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-2xl bg-black px-6 py-3 text-base font-medium text-white shadow-[0_16px_34px_rgba(0,0,0,0.45)] hover:bg-[#121212] transition"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="px-4 py-20 md:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl md:text-5xl font-semibold leading-tight text-slate-950 dark:text-white mb-6">
                Ready when you are.
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-8 max-w-xl mx-auto">
                Free audit, no obligation, 24-hour reply. Or pick up the phone — I answer myself.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <Button
                  onClick={() => document.getElementById('audit-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="h-14 rounded-2xl bg-black text-base font-medium text-white px-8 shadow-[0_16px_34px_rgba(0,0,0,0.45)] hover:bg-[#121212]"
                >
                  Get my free audit
                </Button>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 font-semibold text-emerald-700 underline decoration-2 underline-offset-4 dark:text-emerald-400"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </section>
        </div>

        {/* Floating CTAs */}
        <div
          className={`fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3 transition-all duration-300 ${
            showFloating ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Message us on WhatsApp"
            className="group inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-3 text-sm font-medium text-white shadow-[0_16px_34px_rgba(16,185,129,0.45)] hover:bg-emerald-700 transition"
          >
            <MessageCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Call now</span>
          </a>
          <button
            type="button"
            onClick={scrollToForm}
            aria-label="Get a free audit"
            className="group inline-flex items-center gap-2 rounded-full bg-black px-5 py-3 text-sm font-medium text-white shadow-[0_16px_34px_rgba(0,0,0,0.45)] hover:bg-[#121212] transition"
          >
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            <span>Get free audit</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default GoogleAdsManagement;
